import { apiClient } from '@/services/Axios';
import { unwrapV1, v1Delete, v1Get, v1Post } from '@/services/v1Api';

/**
 * Send supplier request email (authenticated).
 * Creates a token, sends email with link to /subhire-request/:token.
 * POST /api/v1/projects/:projectId/equipment-lists/:equipmentListId/sub-hires/:subhireId/send-supplier-request
 * Body: {
 *   message?: string,
 *   expires_at?: string,  // ISO datetime for optional link expiry
 *   line_ids?: number[]  // Subhire line IDs (kit/equipment to include in the request)
 *   supplier_id?: number // When set, send request to this supplier (multi-supplier workflow)
 *   delivery_preference?: 'we_collect_return' | 'supplier_delivers_collects'  // simplified
 *   inbound_method?: 'we_collect' | 'supplier_delivers' | 'courier'           // explicit (with outbound_method)
 *   outbound_method?: 'we_return' | 'supplier_collects' | 'courier'
 *   delivery_date?: string,   // ISO date → subhire.delivery_datetime (when you receive equipment)
 *   return_date?: string     // ISO date → subhire.collection_datetime (when you return equipment)
 * }
 */
export async function sendSubhireRequest(projectId, equipmentListId, subhireId, payload = {}) {
  const { data } = await v1Post(
    `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/sub-hires/${subhireId}/supplier-requests`,
    payload
  );
  return data;
}

/**
 * Add a supplier to the subhire's quote-request list (persisted, no email sent).
 * Backend creates a draft/pending quote request so the supplier appears in the list and survives refresh.
 * POST /api/v1/projects/:projectId/equipment-lists/:equipmentListId/sub-hires/:subhireId/quote-requests
 * Body: { supplier_id: number, equipment_list_content_id?: number }
 * equipment_list_content_id must be one of the subhire's line IDs (subhire.lines[].equipment_list_content_id).
 * When provided, backend can fill quoted price / source line / estimated unit cost from the EQ list line.
 * Returns: created quote request (e.g. { id, supplier_id, status: 'draft'|'pending' }).
 */
export async function addSupplierToSubhireQuoteRequests(projectId, equipmentListId, subhireId, supplierId, equipmentListContentId = null) {
  const body = { supplier_id: supplierId };
  if (equipmentListContentId != null && equipmentListContentId !== '') {
    body.equipment_list_content_id = Number(equipmentListContentId);
  }
  const { data } = await v1Post(
    `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/sub-hires/${subhireId}/quote-requests`,
    body
  );
  return data;
}

/**
 * Delete a quote request (remove supplier from the subhire's list).
 * DELETE /api/v1/projects/:projectId/equipment-lists/:equipmentListId/sub-hires/:subhireId/quote-requests/:requestId
 */
export async function deleteQuoteRequest(projectId, equipmentListId, subhireId, requestId) {
  await v1Delete(
    `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/sub-hires/${subhireId}/quote-requests/${requestId}`
  );
  return { success: true };
}

/**
 * Confirm a quote request (set subhire supplier and apply quote).
 * POST /api/v1/projects/:projectId/equipment-lists/:equipmentListId/sub-hires/:subhireId/confirm-request
 * Body: { request_id: number }
 */
export async function confirmSubhireRequest(projectId, equipmentListId, subhireId, requestId) {
  const { data } = await v1Post(
    `/api/v1/projects/${projectId}/equipment-lists/${equipmentListId}/sub-hires/${subhireId}/quote-requests/confirm`,
    { request_id: requestId }
  );
  return data;
}

/**
 * Public (no auth required) – supplier opens link from email and views/responds to subhire request.
 * GET /api/v1/subhire-requests/{token}
 * Returns: project, subhire_number, supplier, message, items (requested line items), etc.
 */
export async function getSubhireRequest(token) {
  const { data } = await v1Get(`/api/v1/subhire-requests/${token}`);
  return data;
}

/**
 * Public – supplier submits response (accept/deny or accept with quote details).
 * POST /api/v1/subhire-requests/{token}/respond
 * Body: {
 *   response: 'accepted' | 'denied',
 *   delivery_collection?: string,  // e.g. 'we_collect_deliver' | 'supplier_delivers' | 'supplier_collects' | 'we_deliver_supplier_collects'
 *   notes?: string,
 *   items?: [{ item_id?, name, quantity, days, discount_percent, unit_price }],
 *   discount_amount?: number,  // total of line-item discounts in GBP
 *   delivery_cost?: number,
 *   collection_cost?: number,
 *   includes_delivery_collection?: boolean,  // false when requester collects/returns (quote excludes delivery & collection)
 *   attachment_id?: number
 * }
 */
export async function respondToSubhireRequest(token, body) {
  const { data } = await v1Post(`/api/v1/subhire-requests/${token}/respond`, body);
  return data;
}

/**
 * Upload PDF attachment for subhire response (if API expects separate upload).
 * POST /api/v1/subhire-requests/{token}/attachment or multipart on respond.
 */
export async function uploadSubhireResponseAttachment(token, file) {
  const formData = new FormData();
  formData.append('attachment', file);
  const response = await apiClient.post(`/api/v1/subhire-requests/${token}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  const { data } = unwrapV1(response);
  return data;
}
